import { useCoinMetricsApiKey, useGetAssetMetricsCatalogQuery } from '@src/providers/CoinMetricsApiProvider';
import { useMemo } from 'react';

// One unfiltered request covers all ~2 200 assets in a single page (~6 MB uncompressed,
// ~1.5 MB gzipped). The endpoint is `keepUnusedDataFor: 3600` so the result is held for an
// hour. Callers should pre-warm this query at panel-mount time to hide first-fetch latency
// before the user opens the metric picker.
const CM_CATALOG_PAGE_SIZE = 10_000;

const EMPTY_METRICS: string[] = [];

interface UseAvailableMetricsResult {
  /** Sorted CoinMetrics metric IDs supported by *every* selected asset (set intersection). */
  metrics: string[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Returns the intersection of CoinMetrics metric IDs supported by every selected asset.
 *
 * - Empty `cmAssetIds` resolves to `[]` so the UI can render a disabled "pick an asset
 *   first" state rather than showing the entire catalog.
 * - A selected asset not present in the catalog collapses the intersection to `[]` —
 *   we cannot vouch for metrics on assets we don't know about.
 */
export function useAvailableMetrics(cmAssetIds: string[]): UseAvailableMetricsResult {
  const apiKey = useCoinMetricsApiKey();
  const { data, isLoading, isError, isUninitialized } = useGetAssetMetricsCatalogQuery(
    { page_size: CM_CATALOG_PAGE_SIZE },
    { skip: !apiKey }
  );

  // Pivot the catalog into `asset → Set<metric>` once per response. The catalog payload
  // is already keyed by asset, so this is a linear pass with no joining work.
  const metricsByAsset = useMemo(() => {
    const byAsset = new Map<string, Set<string>>();
    if (!data?.data) {
      return byAsset;
    }
    for (const entry of data.data) {
      const metricIds = new Set<string>();
      for (const metricEntry of entry.metrics ?? []) {
        if (metricEntry.metric) {
          metricIds.add(metricEntry.metric);
        }
      }
      byAsset.set(entry.asset, metricIds);
    }
    return byAsset;
  }, [data]);

  const metrics = useMemo<string[]>(() => {
    if (cmAssetIds.length === 0 || metricsByAsset.size === 0) {
      return EMPTY_METRICS;
    }

    // Start the running intersection from the first asset's metric set, then prune
    // anything not in every subsequent asset's set. Stop early once it's empty.
    let intersection: Set<string> | null = null;
    for (const cmAssetId of cmAssetIds) {
      const assetMetrics = metricsByAsset.get(cmAssetId);
      if (assetMetrics == null) {
        return EMPTY_METRICS;
      }
      if (intersection === null) {
        intersection = new Set(assetMetrics);
        continue;
      }
      for (const metric of intersection) {
        if (!assetMetrics.has(metric)) {
          intersection.delete(metric);
        }
      }
      if (intersection.size === 0) {
        return EMPTY_METRICS;
      }
    }

    return intersection === null ? EMPTY_METRICS : Array.from(intersection).toSorted();
  }, [cmAssetIds, metricsByAsset]);

  return { metrics, isLoading: isLoading || isUninitialized, isError };
}
